// frontend/src/modules/hr/pages/MyPerformancePage.tsx
//
// Employee-facing "My performance" page — my goals + my shared reviews via
// GET /api/hr/performance/me/goals and /me/reviews. No permission key (self
// page, mirrors My schedule's nav entry). Acknowledge is the only write.

import * as React from 'react';
import { toast } from 'sonner';
import { Target, ClipboardList } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { useApi } from '../../../design-system/useApi';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { VISUAL, statusPill } from '../../../design-system/visual';
import { hrApi } from '../api';
import type { PerformanceGoal, PerformanceGoalStatus, PerformanceReview } from '../types';

function goalStatusTone(status: PerformanceGoalStatus): 'info' | 'success' | 'muted' {
  if (status === 'achieved') return 'success';
  if (status === 'dropped') return 'muted';
  return 'info';
}

export function MyPerformancePage() {
  const confirm = useConfirm();
  const goalsQ = useApi<{ goals: PerformanceGoal[] }>('/api/hr/performance/me/goals');
  const reviewsQ = useApi<{ reviews: PerformanceReview[] }>('/api/hr/performance/me/reviews');
  const [acking, setAcking] = React.useState<string | null>(null);

  const notEmployee = goalsQ.status === 404 || reviewsQ.status === 404;

  async function acknowledge(review: PerformanceReview) {
    if (!(await confirm({
      title: 'Acknowledge this review?',
      description: 'This confirms you have read it.',
      confirmLabel: 'Acknowledge',
    }))) return;
    setAcking(review.id);
    try {
      await hrApi.acknowledgeReview(review.id);
      toast.success('Review acknowledged');
      reviewsQ.reload();
    } catch (err: any) {
      toast.error('Could not acknowledge', { description: err.message });
    } finally { setAcking(null); }
  }

  const loading = goalsQ.loading || reviewsQ.loading;

  return (
    <div>
      <PageHeader
        eyebrow="Team / My performance"
        title="My performance"
        description="Your goals and shared reviews."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {loading && <LoadingState variant="page" />}

        {!loading && notEmployee && (
          <section className={[VISUAL.bento, 'p-10 text-center'].join(' ')}>
            <Target className="h-8 w-8 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-foreground">No employee profile found</p>
            <p className="text-[13px] text-muted-foreground mt-1.5 max-w-sm mx-auto">
              You don&apos;t have an employee record yet. Ask your admin to add you to HR to see your performance.
            </p>
          </section>
        )}

        {!loading && !notEmployee && (
          <>
            <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
              <h3 className={[VISUAL.dashSection, 'mb-4'].join(' ')}>Goals</h3>
              {(goalsQ.data?.goals ?? []).length === 0 ? (
                <EmptyState icon={<Target />} title="No goals yet" description="Your manager hasn't set any goals for you yet." />
              ) : (
                <ul className="space-y-3">
                  {goalsQ.data!.goals.map((g) => (
                    <li key={g.id} className="rounded-2xl bg-card/60 dark:bg-white/[0.03] ring-1 ring-border/50 dark:ring-white/10 p-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[13.5px] font-medium text-foreground">{g.title}</p>
                        {g.description && <p className="text-[12px] text-muted-foreground mt-0.5">{g.description}</p>}
                        {g.due_date && <p className="text-[11.5px] text-muted-foreground/80 mt-1">Due {g.due_date}</p>}
                      </div>
                      <span className={statusPill(goalStatusTone(g.status))}>{g.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
              <h3 className={[VISUAL.dashSection, 'mb-4'].join(' ')}>Reviews</h3>
              {(reviewsQ.data?.reviews ?? []).length === 0 ? (
                <EmptyState icon={<ClipboardList />} title="No reviews yet" description="Shared reviews from your manager will show up here." />
              ) : (
                <ul className="space-y-4">
                  {reviewsQ.data!.reviews.map((r) => (
                    <li key={r.id} className="rounded-2xl bg-card/60 dark:bg-white/[0.03] ring-1 ring-border/50 dark:ring-white/10 p-5 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[13.5px] font-medium text-foreground">{r.period_start} – {r.period_end}</p>
                        <span className={statusPill(r.status === 'acknowledged' ? 'success' : 'info')}>{r.status}</span>
                      </div>
                      <p className="text-[12.5px] text-muted-foreground">Overall rating: <span className="font-mono tabular-nums text-foreground">{r.overall_rating} / 5</span></p>
                      {r.strengths && (
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/80">Strengths</p>
                          <p className="text-[12.5px] text-foreground/90">{r.strengths}</p>
                        </div>
                      )}
                      {r.improvements && (
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/80">Areas to improve</p>
                          <p className="text-[12.5px] text-foreground/90">{r.improvements}</p>
                        </div>
                      )}
                      {r.summary && (
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/80">Summary</p>
                          <p className="text-[12.5px] text-foreground/90">{r.summary}</p>
                        </div>
                      )}
                      {r.status === 'shared' && (
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            disabled={acking === r.id}
                            onClick={() => acknowledge(r)}
                            className="rounded-full h-9 px-4 text-[13px] font-semibold"
                          >
                            {acking === r.id ? 'Acknowledging…' : 'Acknowledge'}
                          </Button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
